import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, Music, MapPin, ChevronRight, Loader, Zap, Users, LinkIcon, Calendar, ArrowRight } from 'lucide-react';
import { ref, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../../shared/firebase';
import { getProvinceName } from '../utils/provinces';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function Home() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bandAds, setBandAds] = useState([]);
  const [musicianAds, setMusicianAds] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const adsSnap = await get(ref(database, 'vjb_ads'));
        const profilesSnap = await get(ref(database, 'vjb_profiles'));
        
        const profilesData = profilesSnap.exists() ? profilesSnap.val() : {};
        setProfiles(profilesData);
        
        if (adsSnap.exists()) {
          const adsData = adsSnap.val();
          const allAds = Object.entries(adsData)
            .map(([id, ad]) => ({ id, ...ad }))
            .filter(ad => ad.status === 'active')
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          
          // Split ads by creator's profile type
          const bandList = [];
          const musicianList = [];
          
          allAds.forEach(ad => {
            const creatorProfile = profilesData[ad.createdBy];
            const creatorType = creatorProfile?.type || 'musician';
            
            if (creatorType === 'band') {
              if (bandList.length < 3) bandList.push(ad);
            } else {
              if (musicianList.length < 3) musicianList.push(ad);
            }
          });
          
          setBandAds(bandList);
          setMusicianAds(musicianList);
        }
      } catch (error) {
        console.error('Error loading ads:', error);
      }
      setLoading(false);
    };
    
    loadData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/zoeken?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/zoeken');
    }
  };

  const getCreatorInfo = (ad) => {
    const profile = profiles[ad.createdBy];
    return {
      name: profile?.displayName || 'Onbekend',
      photoUrl: profile?.photoUrl || null,
      type: profile?.type || 'musician'
    };
  };

  const AdCard = ({ ad }) => {
    const creator = getCreatorInfo(ad);
    return (
      <Link 
        to={`/oproep/${ad.id}`}
        className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#8146FF]/50 rounded-2xl overflow-hidden transition-all duration-300"
      >
        <div className="aspect-[16/10] bg-gradient-to-br from-[#8146FF]/30 to-black relative overflow-hidden">
          {creator.photoUrl ? (
            <img 
              src={creator.photoUrl} 
              alt={creator.name}
              className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <User size={48} className="text-white/20" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
          <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
            {ad.lookingFor?.slice(0, 2).map((item, idx) => (
              <span key={idx} className="bg-[#8146FF] text-white text-xs font-medium px-2 py-1 rounded-full">{item}</span>
            ))}
            {ad.lookingFor?.length > 2 && (
              <span className="bg-white/20 text-white text-xs font-medium px-2 py-1 rounded-full">+{ad.lookingFor.length - 2}</span>
            )}
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-lg mb-2 group-hover:text-[#8146FF] transition line-clamp-1">{ad.title}</h3>
          <p className="text-white/50 text-sm mb-4 line-clamp-2">{ad.description}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {creator.photoUrl ? (
                <img src={creator.photoUrl} alt={creator.name} className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center"><User size={12} className="text-white/40" /></div>
              )}
              <span className="text-white/60 text-sm">{creator.name}</span>
            </div>
            <div className="flex items-center gap-1 text-white/40 text-sm">
              <MapPin size={12} />
              {ad.location?.city || getProvinceName(ad.location?.province)}
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const AdSection = ({ title, ads, filterType }) => (
    <section className="py-8 md:py-12">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-bold">{title}</h2>
          <Link 
            to={`/zoeken?type=${filterType}`}
            className="text-[#8146FF] hover:text-[#a074ff] flex items-center gap-1 font-medium text-sm"
          >
            Bekijk alles
            <ChevronRight size={18} />
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader className="animate-spin text-[#8146FF]" size={32} />
          </div>
        ) : ads.length === 0 ? (
          <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl">
            <p className="text-white/60 mb-4">Nog geen oproepen in deze categorie.</p>
            <Link 
              to="/oproep/nieuw" 
              className="inline-block bg-[#8146FF] hover:bg-[#6a35d9] px-6 py-3 rounded-full font-medium transition"
            >
              Plaats een oproep
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ads.map((ad) => <AdCard key={ad.id} ad={ad} />)}
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#8146FF]/20 via-black to-black" />
        
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Vind jouw nieuwe
              <span className="text-[#8146FF]"> bandlid</span>
            </h1>
            <p className="text-lg md:text-xl text-white/60 mb-8 max-w-2xl">
              Het platform waar muzikanten en bands elkaar vinden. 
              Plaats een oproep of reageer op zoekende bands in Nederland en België.
            </p>

            {/* Search bar */}
            <form onSubmit={handleSearch} className="flex gap-3 max-w-xl">
              <div className="flex-1 relative">
                <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Zoek op instrument, genre, locatie..."
                  className="w-full bg-white/10 border border-white/20 rounded-full pl-12 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF] focus:bg-white/15 transition"
                />
              </div>
              <button 
                type="submit"
                className="bg-[#8146FF] hover:bg-[#6a35d9] px-6 py-3 rounded-full font-medium transition flex items-center gap-2"
              >
                <Search size={18} />
                <span className="hidden sm:inline">Zoeken</span>
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Band zoekt muzikant */}
      <AdSection 
        title="Band zoekt muzikant" 
        ads={bandAds} 
        filterType="band"
      />

      {/* Muzikant zoekt band */}
      <AdSection 
        title="Muzikant zoekt band" 
        ads={musicianAds} 
        filterType="musician"
      />

      {/* Tapsody Integration Section */}
      <section className="py-16 md:py-20 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left: Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded-full text-sm font-medium mb-4">
                <LinkIcon size={14} />
                Tapsody integratie
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Gebruik je Tapsody? <br />
                <span className="text-[#8146FF]">Je bent al klaar!</span>
              </h2>
              <p className="text-white/60 mb-6">
                VindJouwBandLid werkt naadloos samen met Tapsody.app! Log in met je Tapsody-account 
                en je bandgegevens, genres en locatie worden automatisch ingevuld. Zo kun je direct 
                beginnen met het zoeken of plaatsen van oproepen.
              </p>
              
              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#8146FF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Users size={16} className="text-[#8146FF]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">Bandprofiel automatisch gevuld</h4>
                    <p className="text-white/50 text-sm">Je bandnaam, genres en locatie worden direct overgenomen.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#8146FF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Calendar size={16} className="text-[#8146FF]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">Eén account, twee platforms</h4>
                    <p className="text-white/50 text-sm">Beheer je band én vind nieuwe leden met dezelfde login.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link 
                  to={user ? "/profiel/bewerken" : "/inloggen"}
                  className="inline-flex items-center justify-center gap-2 bg-[#8146FF] hover:bg-[#6a35d9] px-6 py-3 rounded-full font-medium transition"
                >
                  {user ? 'Profiel aanmaken' : 'Inloggen met Tapsody'}
                  <ArrowRight size={18} />
                </Link>
                <a 
                  href="https://tapsody.app" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 px-6 py-3 rounded-full font-medium transition"
                >
                  Wat is Tapsody?
                </a>
              </div>
            </div>

            {/* Right: Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[#8146FF]/20 to-emerald-500/10 rounded-3xl p-6 md:p-8 border border-white/10">
                {/* Mock profile card */}
                <div className="bg-white/5 rounded-2xl p-5 border border-white/10 mb-4">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#8146FF] to-emerald-500 flex items-center justify-center">
                      <Users size={24} className="text-white" />
                    </div>
                    <div>
                      <div className="h-4 w-32 bg-white/20 rounded mb-2"></div>
                      <div className="h-3 w-20 bg-white/10 rounded"></div>
                    </div>
                    <div className="ml-auto">
                      <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-1 rounded-full">
                        Tapsody ✓
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="bg-[#8146FF]/30 text-[#a074ff] text-xs px-2 py-1 rounded-full">Rock</span>
                    <span className="bg-[#8146FF]/30 text-[#a074ff] text-xs px-2 py-1 rounded-full">Indie</span>
                    <span className="bg-[#8146FF]/30 text-[#a074ff] text-xs px-2 py-1 rounded-full">Alternative</span>
                  </div>
                  <div className="flex items-center gap-1 text-white/40 text-sm">
                    <MapPin size={12} />
                    <span>Amsterdam, Noord-Holland</span>
                  </div>
                </div>

                {/* Sync indicator */}
                <div className="flex items-center justify-center gap-3 text-white/50 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <span className="text-emerald-400 font-bold text-xs">T</span>
                    </div>
                    <span>Tapsody</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                    <ArrowRight size={14} className="text-emerald-400" />
                    <div className="w-2 h-2 rounded-full bg-[#8146FF] animate-pulse"></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#8146FF]/20 flex items-center justify-center">
                      <span className="text-[#8146FF] font-bold text-xs">VJB</span>
                    </div>
                    <span>VindJouwBandLid</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">Waarom VindJouwBandLid?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#8146FF]/20 flex items-center justify-center">
                <Zap size={28} className="text-[#8146FF]" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Slimme matching</h3>
              <p className="text-white/50">Ons algoritme vindt muzikanten die bij jou passen op basis van genre, locatie en ervaring.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#8146FF]/20 flex items-center justify-center">
                <Users size={28} className="text-[#8146FF]" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Directe communicatie</h3>
              <p className="text-white/50">Geen gedoe met e-mails. Chat direct via het platform met potentiële bandleden.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#8146FF]/20 flex items-center justify-center">
                <Music size={28} className="text-[#8146FF]" />
              </div>
              <h3 className="font-semibold text-lg mb-2">100% gratis</h3>
              <p className="text-white/50">Maak je profiel, plaats oproepen en neem contact op, helemaal gratis.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="bg-gradient-to-br from-[#8146FF] to-[#5c2eb8] rounded-3xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-4">Klaar om te starten?</h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">Maak binnen 2 minuten je profiel aan en ontdek muzikanten in jouw regio.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={user ? "/oproep/nieuw" : "/inloggen"} className="bg-white text-[#8146FF] hover:bg-white/90 px-8 py-3 rounded-full font-semibold transition">
                Plaats een oproep
              </Link>
              <Link to="/zoeken" className="bg-white/20 hover:bg-white/30 px-8 py-3 rounded-full font-semibold transition border border-white/30">
                Bekijk oproepen
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
